import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  Close,
  DeleteForeverOutlined,
  DeleteOutline,
  DriveFileMoveOutlined,
  FileDownloadOutlined,
  RestoreFromTrashOutlined,
} from "@mui/icons-material";
import { Fragment, type ReactElement, type ReactNode } from "react";

import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import { CALIBRATE_COLORS, NAV_SECTION_COLORS } from "components/hoc/Theme";
import useResponsive from "hooks/useResponsive";

import type { BulkAction, BulkActionKey } from "./selectionModel";

const ICONS: Record<BulkActionKey, ReactElement> = {
  add_to_project: <DriveFileMoveOutlined fontSize="small" />,
  add_to_collection: <DriveFileMoveOutlined fontSize="small" />,
  move_files: <DriveFileMoveOutlined fontSize="small" />,
  move_to_project: <DriveFileMoveOutlined fontSize="small" />,
  download: <FileDownloadOutlined fontSize="small" />,
  delete: <DeleteOutline fontSize="small" />,
  restore: <RestoreFromTrashOutlined fontSize="small" />,
  purge: <DeleteForeverOutlined fontSize="small" />,
};

/** A disabled button says why on hover: MUI drops events of disabled buttons,
 * so the tooltip wraps a span. */
function Why({
  reason,
  children,
}: {
  reason: string | null;
  children: ReactElement;
}) {
  if (!reason) return children;
  return (
    <Tooltip title={reason}>
      <span>{children}</span>
    </Tooltip>
  );
}

const DANGER_SX = {
  color: CALIBRATE_COLORS.dangerText,
  borderColor: CALIBRATE_COLORS.dangerText,
  "&:hover": {
    borderColor: CALIBRATE_COLORS.dangerText,
    bgcolor: CALIBRATE_COLORS.dangerHover,
  },
} as const;

/** Takes the place of the toolbar while files are selected: how many, a way to
 * take the whole list, and what can be done with them. */
export default function SelectionBar({
  label,
  selectAll,
  actions,
  readOnly,
  busy,
  progress,
  onAction,
  onClear,
}: {
  /** "3 files selected". */
  label: string;
  /** "Select all 47 files", when there is more to take. */
  selectAll: ReactNode;
  /** In the order shown (selectionModel.bulkActions). */
  actions: BulkAction[];
  readOnly: boolean;
  busy: boolean;
  /** What a running download is doing. */
  progress: string | null;
  onAction: (action: BulkActionKey) => void;
  onClear: () => void;
}) {
  const { isPhone } = useResponsive();

  const button = (each: BulkAction, disabled: boolean) =>
    isPhone ? (
      <IconButton
        size="small"
        aria-label={each.label}
        disabled={disabled}
        onClick={() => onAction(each.action)}
        sx={each.danger ? { color: CALIBRATE_COLORS.dangerText } : undefined}
      >
        {ICONS[each.action]}
      </IconButton>
    ) : (
      <Button
        variant="outlined"
        size="small"
        startIcon={ICONS[each.action]}
        disabled={disabled}
        onClick={() => onAction(each.action)}
        sx={each.danger ? DANGER_SX : undefined}
      >
        {each.label}
      </Button>
    );

  // What changes files stays visible but off in Client View.
  const render = (each: BulkAction) =>
    each.write && readOnly ? (
      <ReadOnlyTooltip active>{button(each, true)}</ReadOnlyTooltip>
    ) : (
      <Why reason={each.disabled}>
        {button(each, busy || Boolean(each.disabled))}
      </Why>
    );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: { xs: 1.25, sm: 1.5 },
        minHeight: 72,
        flexShrink: 0,
        // The Workspace tint of the page's header icon.
        bgcolor: NAV_SECTION_COLORS.workspace.iconBackground,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        <Check fontSize="small" sx={{ color: "primary.main" }} aria-hidden />
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "primary.main" }}
        >
          {label}
        </Typography>
        {selectAll}
        {progress ? (
          <Typography variant="body2" color="text.secondary">
            {progress}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          ml: "auto",
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {actions.map((each) => (
          <Fragment key={each.action}>{render(each)}</Fragment>
        ))}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        {isPhone ? (
          <IconButton
            size="small"
            aria-label="Clear selection"
            onClick={onClear}
          >
            <Close fontSize="small" />
          </IconButton>
        ) : (
          <Button
            size="small"
            startIcon={<Close fontSize="small" />}
            onClick={onClear}
            aria-label="Clear selection"
            sx={{ color: "text.secondary" }}
          >
            Clear
          </Button>
        )}
      </Box>
    </Box>
  );
}

// Pixel strings: in sx a bare 1 means 100%.
const SR_ONLY_SX = {
  position: "absolute" as const,
  width: "1px",
  height: "1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap" as const,
  border: 0,
  p: 0,
  m: "-1px",
};

/** Says what the selection is now to a screen reader, out of sight. */
export function SelectionAnnouncement({ text }: { text: string }) {
  return (
    <Box role="status" aria-live="polite" sx={SR_ONLY_SX}>
      {text}
    </Box>
  );
}

/** "Select all 47 files" as a link-looking button. */
export function SelectAllLink({
  total,
  busy,
  onClick,
}: {
  total: number;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      disabled={busy}
      onClick={onClick}
      sx={{ fontWeight: 600 }}
    >
      {busy ? "Selecting..." : `Select all ${total} files`}
    </Link>
  );
}
